import { Drawer as MantineDrawer } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import type { ReactNode } from "react";
import { Button } from "./Button";
import { STRINGS } from "./strings";

/* A record opens over the list rather than replacing it.
 *
 * On a wide screen it slides in from the right at the panel width and the list
 * stays behind it, so closing returns the person to their place. On a phone it
 * is the whole screen with Back in its header, and Back returns to the list at
 * the same scroll position. The URL does not change between the two: the open
 * record is ?record= on both. docs/design-standards.md, One interface, every
 * screen size.
 *
 * Escape closes it, focus moves into it on open and back to where it was on
 * close, and the title is what a screen reader announces. The kit does that
 * part, and it is why the kit is here. */
export const NARROW = "(max-width: 48em)";

export function Drawer({ title, subtitle, onClose, children }: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const narrow = useMediaQuery(NARROW);
  return (
    <MantineDrawer.Root
      opened
      onClose={onClose}
      position="right"
      size={narrow ? "100%" : "min(46rem, 100vw)"}
      removeScrollProps={{ allowPinchZoom: true }}
    >
      <MantineDrawer.Overlay backgroundOpacity={0.18} />
      <MantineDrawer.Content className="ui-drawer">
        <MantineDrawer.Header className="ui-drawer-head">
          {narrow ? (
            <Button kind="link" onClick={onClose} className="ui-drawer-back">{STRINGS.back}</Button>
          ) : null}
          <div className="ui-drawer-titles">
            <MantineDrawer.Title className="ui-drawer-title">{title}</MantineDrawer.Title>
            {subtitle ? <p className="muted ui-drawer-subtitle">{subtitle}</p> : null}
          </div>
          {narrow ? null : <MantineDrawer.CloseButton aria-label={STRINGS.close} />}
        </MantineDrawer.Header>
        <MantineDrawer.Body className="ui-drawer-body">{children}</MantineDrawer.Body>
      </MantineDrawer.Content>
    </MantineDrawer.Root>
  );
}
